import { getNowPlainDateISO } from '@/lib/time-zones.js';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { canCreateUsers } from '@/auth/roles.js';
import { useSession } from '@/auth/useSession.js';
import { InfoPage } from '@/components/info-page.js';
import { StudentBulkUpdate } from '@/components/students/student-bulk-update.js';
import { StudentStatusBadge } from '@/components/students/student-status-badge.js';
import { EntityTable, createTableRowSelectionCell } from '@/components/table/entity-table.js';
import { TableCellWithPopover } from '@/components/table/table-cell-with-popover.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { Button } from '@/components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';

import { EmptyArray, assignManualOnDemandGLA, getStudents } from '@/lib/api-calls.js';
import { buildClientExportColumns, exportToCSV } from '@/lib/csv-utils.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { DEFAULT_PAGE_SIZE, pageSizeSchemaWithMax, paginationRequestSchema } from '@/lib/pagination.js';
import { generateQrCodesPdf } from '@/lib/qr-code-pdf-utils.js';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing.js';

import { useAllSchools, useOrgOptions, useSchoolAndOrgOptions } from '@/lib/api-call-hooks.js';
import { useRowsBulkSelection } from '@/lib/bulk-selection.js';
import { airServerErrorFrom, useErrorToast } from '@/lib/error-handling.js';
import { useSorting } from '@/lib/sorting.js';
import {
  calculateAttendanceRate,
  formatAttendedSessionDatesFromFeedback,
  getStudentStatusLabel,
  getStudentStatusOptions,
  getStudentYearOptions,
} from '@/lib/student-utils.js';
import { airColumnHelper } from '@/lib/tables.js';

import { useTimeZone } from '@/auth/useTimeZone.js';
import { StudentShortLink } from '@/components/join-now/student-short-link.js';
import { Tooltip } from '@/components/tooltip.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { getDistrictColumn } from '@/lib/district-utils.js';
import { CreateStudent } from '@/routes/_layout/users/-components/create-student.js';
import { CreateStudentsByCsv } from '@/routes/_layout/users/-components/create-students-by-csv.js';
import { formatFutureSessionColumnLists, formatPlainDate, isAirAdmin } from '@airreading/common';
import {
  LARGE_MAX_PAGE_SIZE,
  StudentListRow,
  GetStudentsSortableColumn,
  StudentStatus,
  StudentYearColorMap,
  UpdateStudentsBulkActions,
  getStudentStateIdDisplay,
  getStudentsSortOrderSchema,
  getStudentsSortableColumnSchema,
  studentStatusSchema,
  studentYearOption,
  studentYearOptionOnlyNumber,
  studentYearSchema,
} from '@airreading/common/data';
import { BulkUpdateLearningPathByCsv } from './-components/bulk-update-learning-path-by-csv.js';
import { studentsTabs } from './-components/tabs.js';
const studentsSearchSchema = paginationRequestSchema.extend({
  pageSize: pageSizeSchemaWithMax(LARGE_MAX_PAGE_SIZE, DEFAULT_PAGE_SIZE),
  query: z.string().optional().catch(''),
  schools: z.array(z.string()).optional(),
  orgs: z.array(z.string()).optional(),
  grades: z.array(studentYearSchema).optional(),
  statuses: z.array(studentStatusSchema).optional(),
  sortBy: getStudentsSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/students/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: StudentsIndex,
  validateSearch: studentsSearchSchema,
});

const col = airColumnHelper<StudentListRow>(getStudentsSortableColumnSchema);

function futureSessionColumnLists(sessions: StudentListRow['studentSessions'], timeZone: string) {
  return formatFutureSessionColumnLists(
    (sessions ?? []).map((session) => ({
      startInstant: session.startInstant,
      lessonName: session.lesson?.name ?? null,
    })),
    timeZone,
  );
}

const buildColumns = (
  timeZone: string,
  schoolIdToDistrictName: Record<string, string>,
  isSelectAllPagesActive: boolean,
  onSelectAllPagesChange: (isSelectAllActive: boolean) => void,
  onAssignGLA: (studentId: string) => void,
) => [
  createTableRowSelectionCell(col, { isSelectAllPagesActive, onSelectAllPagesChange }),
  col.accessor((row) => row.id, {
    id: 'fullNameOrEmail',
    header: 'Name',
    cell: ({ row, getValue }) => (
      <Link
        to={'/students/$studentId'}
        params={{ studentId: getValue() }}
        search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
      >
        {row.original.fullNameOrEmail}
      </Link>
    ),
  }),
  col.accessor('shortCode', {
    header: 'Direct join short link',
    id: 'shortLink',
    cell: ({ getValue }) => {
      const shortLink = getValue();
      return <StudentShortLink shortCode={shortLink} />;
    },
  }),
  col.accessor('email', {
    id: 'email',
    header: 'Email',
  }),
  col.accessor((row) => getStudentStateIdDisplay(row) ?? '-', {
    id: 'stateId',
    header: 'State ID',
    enableSorting: false,
  }),
  col.accessor('grade', {
    id: 'grade',
    header: 'Grade',
    cell: ({ getValue }) => {
      const grade = getValue();
      if (!grade) return studentYearOption(grade);
      const backgroundColor = StudentYearColorMap[grade];
      return (
        <div className="min-w-[80px] flex justify-start">
          <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
            {studentYearOption(grade)}
          </span>
        </div>
      );
    },
  }),
  col.accessor((row) => row, {
    id: 'school',
    header: 'School',
    enableSorting: false,
    cell: ({ getValue }) => {
      const school = getValue()?.school;
      return (
        school && (
          <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
            {school.name}
          </Link>
        )
      );
    },
  }),
  col.accessor('school', {
    id: 'district',
    header: 'District',
    cell: ({ getValue }) => {
      const school = getValue();
      const districtName = school?.id ? schoolIdToDistrictName[school.id] : null;
      if (!districtName) return <span>Unknown</span>;
      return school
        ? getDistrictColumn({
            orgId: school.orgId,
            orgName: districtName,
          })
        : null;
    },
  }),
  col.accessor('totalFutureSessions', {
    id: 'totalFutureSessions',
    header: 'Total future sessions',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ?? 0;
    },
  }),
  col.accessor('nextSessionPlainDate', {
    header: 'Next session date',
    id: 'nextSessionPlainDate',
    cell: ({ getValue }) => {
      const date = getValue();
      return date === null ? '' : formatPlainDate(date);
    },
  }),
  col.accessor('studentSessions', {
    id: 'allFutureSessionDates',
    header: 'All future session dates',
    cell: ({ getValue }) => {
      const { dates } = futureSessionColumnLists(getValue(), timeZone);
      if (dates.length === 0) return '';
      return <TableCellWithPopover items={dates} />;
    },
  }),
  col.accessor('studentSessions', {
    id: 'allFutureSessionLessons',
    header: 'All future session lessons',
    cell: ({ getValue }) => {
      const { lessons } = futureSessionColumnLists(getValue(), timeZone);
      if (lessons.length === 0) return '';
      return <TableCellWithPopover items={lessons} />;
    },
  }),
  col.accessor('canLaunchManualGLA', {
    id: 'launchGLA',
    header: 'Launch GLA',
    cell: ({ getValue, row }) => {
      const canLaunch = getValue();
      const button = (
        <Button
          size="sm"
          disabled={!canLaunch}
          onClick={(e) => {
            e.stopPropagation();
            onAssignGLA(row.original.id);
          }}
        >
          Assign
        </Button>
      );
      if (canLaunch) return button;
      return <Tooltip trigger={button}>Not eligible</Tooltip>;
    },
  }),
  col.accessor('status', {
    id: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      return (
        <div className="flex gap-x-1">
          <StudentStatusBadge status={getValue()} />
        </div>
      );
    },
  }),
  col.accessor('studentSessions', {
    id: 'attendance-all-past-sessions',
    header: 'Attendance - all past sessions',
    cell: ({ row }) => {
      const sessionFeedbacks = row.original.sessionFeedbacks;
      return `${calculateAttendanceRate(sessionFeedbacks)}%`;
    },
  }),
  col.accessor('studentSessions', {
    id: 'attendance-all-past-sessions-last-14-days',
    header: 'Attendance - past 14 days',
    cell: ({ row }) => {
      const sessionFeedbacks = row.original.sessionFeedbacks;
      return `${calculateAttendanceRate(sessionFeedbacks, 14)}%`;
    },
  }),
  col.accessor('studentSessions', {
    id: 'attendance-all-past-sessions-number',
    header: 'Number of attendance - all past sessions',
    cell: ({ row }) => {
      const sessionFeedbacks = row.original.sessionFeedbacks;
      return (
        sessionFeedbacks?.filter((feedback) => feedback.studentAttended === true && feedback.status === 'completed')
          ?.length ?? 0
      );
    },
  }),
  col.accessor('studentSessions', {
    id: 'attendance-all-past-sessions-dates',
    header: 'All dates of attendance - all past sessions',
    cell: ({ row }) => {
      const sessionFeedbacks = row.original.sessionFeedbacks;
      const datesString = formatAttendedSessionDatesFromFeedback(sessionFeedbacks, timeZone);
      if (!datesString) return '';

      const dates = datesString.split(', ').filter((date) => date.trim() !== '');
      return <TableCellWithPopover items={dates} />;
    },
  }),
  col.accessor('phonicsSkill1Mastered', {
    id: 'phonicsSkill1Mastered',
    header: 'Phonics skill 1 mastered',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ?? 0;
    },
  }),
  col.accessor('phonicsSkill2Mastered', {
    id: 'phonicsSkill2Mastered',
    header: 'Phonics skill 2 mastered',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ?? 0;
    },
  }),
  col.accessor('phonicsSkill3Mastered', {
    id: 'phonicsSkill3Mastered',
    header: 'Phonics skill 3 mastered',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ?? 0;
    },
  }),
  col.accessor('morphemeSkill1Mastered', {
    id: 'morphemeSkill1Mastered',
    header: 'Morpheme skill 1 mastered',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ?? 0;
    },
  }),
  col.accessor('morphemeSkill2Mastered', {
    id: 'morphemeSkill2Mastered',
    header: 'Morpheme skill 2 mastered',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ?? 0;
    },
  }),
];

function StudentsIndex() {
  const { timeZone } = useTimeZone();
  const { data: allSchools } = useAllSchools();
  const { toast } = useToast();
  const { actingUser } = useSession();

  const schoolIdToDistrictName = useMemo(
    () => Object.fromEntries(allSchools.map((school) => [school.id, school.org.name])),
    [allSchools],
  );
  const [isSelectAllPagesActive, setIsSelectAllPagesActive] = useState(false);
  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  // Default sort by student names alphabetically
  const effectiveSortBy = sortBy ?? ['fullNameOrEmail-asc'];
  const { sorting, ...tableSortingOptions } = useSorting<GetStudentsSortableColumn>([effectiveSortBy, didChangeSortBy]);

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[userNameQuery, activeUserNameQuery], setUserNameQuery],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      orgs: [[selectedOrgs, activeSelectedOrgs], setSelectedOrgs],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
      statuses: [[selectedStatuses, activeSelectedStatuses], setSelectedStatuses],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'schools', 'orgs', 'grades', 'statuses']);

  const allOrganizationOptions = useOrgOptions();
  const allSchoolOptions = useSchoolAndOrgOptions({
    orgIds: activeSelectedOrgs ?? [],
  });
  const {
    data: response,
    isPending,
    error,
    refetch,
  } = useQuery(
    getStudents({
      pageIndex,
      pageSize,
      filters: {
        query: activeUserNameQuery,
        schools: activeSelectedSchools,
        orgs: activeSelectedOrgs,
        grades: activeSelectedGrades,
        statuses: activeSelectedStatuses,
      },
      sortBy: effectiveSortBy,
    }),
  );

  // Lazy query implementation - only executes when refetchAllStudents is called
  const { refetch: refetchAllStudents } = useQuery({
    ...getStudents({
      pageIndex: 0,
      pageSize: 0,
      filters: {
        query: activeUserNameQuery,
        schools: activeSelectedSchools,
        orgs: activeSelectedOrgs,
        grades: activeSelectedGrades,
        statuses: activeSelectedStatuses,
      },
      sortBy: effectiveSortBy,
      isAllPages: true,
    }),
    enabled: false, // This prevents automatic execution
  });

  const students = useMemo(() => response?.students || EmptyArray, [response]);
  useErrorToast(error, 'Error loading students. Please try again.');

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<StudentListRow>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => students.find((student) => student.id === id),
    });

  // Handle isSelectAllPagesActive changes and reset selections when deselecting all
  const handleSelectAllPagesChange = useCallback(
    (isActive: boolean) => {
      setIsSelectAllPagesActive(isActive);
      if (!isActive) {
        // When deselecting all pages, reset all row selections
        resetAllRowSelection();
      }
    },
    [resetAllRowSelection],
  );

  // Select all rows on current page when isSelectAllPagesActive is true and students change
  useEffect(() => {
    if (isSelectAllPagesActive && students.length > 0) {
      const allRowIds = students.reduce(
        (acc, student) => {
          acc[student.id] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      );
      onRowSelectionChange(allRowIds);
    }
  }, [students, isSelectAllPagesActive, onRowSelectionChange]);

  const { mutate: assignGLA } = useMutation({
    mutationFn: assignManualOnDemandGLA,
    onSuccess: () => {
      toast({ title: 'On-demand GLA is assigned to the next session successfully' });
      refetch();
    },
    onError: (e) => {
      toast({ title: airServerErrorFrom(e)?.userFacingMessage || 'Failed to assign manual GLA' });
    },
  });

  const handleAssignGLA = useCallback((studentId: string) => assignGLA(studentId), [assignGLA]);

  // Build columns with select all pages support
  const columns = useMemo(
    () =>
      buildColumns(
        timeZone,
        schoolIdToDistrictName,
        isSelectAllPagesActive,
        handleSelectAllPagesChange,
        handleAssignGLA,
      ),
    [timeZone, schoolIdToDistrictName, isSelectAllPagesActive, handleSelectAllPagesChange, handleAssignGLA],
  );

  //get row count
  const totalRowCount = useMemo(() => response?.total || 0, [response]);

  // Reset isSelectAllPagesActive when filters change
  useEffect(() => {
    if (isSelectAllPagesActive) {
      setIsSelectAllPagesActive(false);
      resetAllRowSelection();
    }
  }, [
    activeUserNameQuery,
    activeSelectedSchools,
    activeSelectedOrgs,
    activeSelectedGrades,
    activeSelectedStatuses,
    isSelectAllPagesActive,
    resetAllRowSelection,
  ]);

  const onSuccess = () => {
    setIsSelectAllPagesActive(false);
    resetAllRowSelection();
    refetch();
  };

  // CSV Export functionality
  const exportColumns = useMemo(() => {
    const overrides = {
      fullNameOrEmail: (row: StudentListRow) => row.fullNameOrEmail,
      stateId: (row: StudentListRow) => getStudentStateIdDisplay(row) ?? '-',
      grade: (row: StudentListRow) => (row.grade ? studentYearOptionOnlyNumber(row.grade) : ''),
      school: (row: StudentListRow) => row.school?.name || '',
      district: (row: StudentListRow) => {
        const school = row.school;
        const districtName = school?.id ? schoolIdToDistrictName[school.id] : null;
        return districtName || 'Unknown';
      },
      nextSessionPlainDate: (row: StudentListRow) => {
        const date = row.nextSessionPlainDate;
        return date === null ? '' : formatPlainDate(date);
      },
      status: (row: StudentListRow) => getStudentStatusLabel(row.status),
      'attendance-all-past-sessions': (row: StudentListRow) => {
        const rate = calculateAttendanceRate(row.sessionFeedbacks ?? []);
        return `${rate ?? 0}%`;
      },
      attendanceRate: (row: StudentListRow) => {
        const rate = calculateAttendanceRate(row.sessionFeedbacks ?? []);
        return `${rate ?? 0}%`;
      },
      'attendance-all-past-sessions-last-14-days': (row: StudentListRow) => {
        const rate = calculateAttendanceRate(row.sessionFeedbacks ?? [], 14);
        return `${rate ?? 0}%`;
      },
      'attendance-all-past-sessions-number': (row: StudentListRow) => {
        const sessionFeedbacks = row.sessionFeedbacks ?? [];
        return sessionFeedbacks.filter(
          (feedback) => feedback.studentAttended === true && feedback.status === 'completed',
        ).length;
      },
      'attendance-all-past-sessions-dates': (row: StudentListRow) => {
        return formatAttendedSessionDatesFromFeedback(row.sessionFeedbacks ?? [], timeZone) || '';
      },
      lastSessionDate: (row: StudentListRow) => {
        const dates = formatAttendedSessionDatesFromFeedback(row.sessionFeedbacks ?? [], timeZone);
        return dates || '';
      },
      specialEd: (row: StudentListRow) => (row.specialEd ? 'Yes' : 'No'),
      dyslexic: (row: StudentListRow) => (row.dyslexic ? 'Yes' : 'No'),
      ell: (row: StudentListRow) => (row.ell ? 'Yes' : 'No'),
      phonicsSkill1Mastered: (row: StudentListRow) => row.phonicsSkill1Mastered ?? 0,
      phonicsSkill2Mastered: (row: StudentListRow) => row.phonicsSkill2Mastered ?? 0,
      phonicsSkill3Mastered: (row: StudentListRow) => row.phonicsSkill3Mastered ?? 0,
      morphemeSkill1Mastered: (row: StudentListRow) => row.morphemeSkill1Mastered ?? 0,
      morphemeSkill2Mastered: (row: StudentListRow) => row.morphemeSkill2Mastered ?? 0,
    };
    return buildClientExportColumns(columns, overrides);
  }, [columns, schoolIdToDistrictName, timeZone]);

  const table = useReactTable({
    data: students,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
      rowSelection,
    },
    rowCount: response?.total || 0,
    onRowSelectionChange,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    ...tableSortingOptions,
    enableColumnResizing: true,
  });

  useEffect(() => {
    if (!selectedSchools || selectedSchools.length === 0 || !selectedOrgs || selectedOrgs?.length === 0) return;

    const areAllSelectedSchoolsInSelectedDistricts = allSchoolOptions
      .filter((allSchool) => selectedSchools?.includes(allSchool.value))
      .map((item) => item.value);

    setSelectedSchools(areAllSelectedSchoolsInSelectedDistricts);
    if (areAllSelectedSchoolsInSelectedDistricts.length === selectedSchools.length) return;
    toast({
      title: 'The selected school was removed because it doesn’t belong to the chosen district.',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSchoolOptions]);

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search users by name, email or state ID"
          value={userNameQuery ?? ''}
          onChange={(event) => {
            setUserNameQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="Districts"
          options={allOrganizationOptions}
          selectedValues={selectedOrgs ?? []}
          onChange={setSelectedOrgs}
        />
        <TableFilterDropdown
          title="Schools"
          options={allSchoolOptions}
          selectedValues={selectedSchools ?? []}
          onChange={setSelectedSchools}
        />
        <TableFilterDropdown
          title="Grades"
          options={getStudentYearOptions()}
          selectedValues={selectedGrades ?? []}
          onChange={setSelectedGrades}
        />
        <TableFilterDropdown
          title="Status"
          options={getStudentStatusOptions()}
          selectedValues={selectedStatuses ?? []}
          onChange={setSelectedStatuses}
        />
        <TableFilterResetButton show={true} onClick={resetFilters} />
      </>
    ),
    [
      userNameQuery,
      setUserNameQuery,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      allOrganizationOptions,
      selectedOrgs,
      setSelectedOrgs,
      selectedGrades,
      setSelectedGrades,
      selectedStatuses,
      setSelectedStatuses,
      resetFilters,
    ],
  );

  const bulkActionsMenuItems = (
    <>
      <DropdownMenuItem
        onSelect={async (e) => {
          e.preventDefault();
          const date = getNowPlainDateISO(timeZone).toString();

          if (isSelectAllPagesActive) {
            // Export all students with filters applied (without pagination)
            const result = await refetchAllStudents();
            const allStudents = result.data?.students || [];
            exportToCSV(allStudents, exportColumns, `students_export_${date}.csv`);
          } else {
            // Export only selected rows from current page
            const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original);
            exportToCSV(selectedRows, exportColumns, `students_export_${date}.csv`);
          }
        }}
      >
        Export students info
      </DropdownMenuItem>
      <StudentBulkUpdate
        confirmMessage="Are you sure you want to enroll"
        invalidMessage="Only dropped or program concluded students can be selected."
        options={{ status: StudentStatus.enrolled }}
        onSuccess={onSuccess}
        students={rowDataSelection}
        title="Mark enrolled"
        validation={(student) =>
          student.status === StudentStatus.dropped || student.status === StudentStatus.program_concluded
        }
        isSelectAllPagesActive={isSelectAllPagesActive}
        filters={{
          query: activeUserNameQuery,
          schools: activeSelectedSchools,
          orgs: activeSelectedOrgs,
          grades: activeSelectedGrades,
          statuses: activeSelectedStatuses,
        }}
        actionName={UpdateStudentsBulkActions.enroll}
        submitButtonVariant="default"
      />
      <StudentBulkUpdate
        confirmMessage="Are you sure you want to drop"
        invalidMessage="Only enrolled students can be selected."
        options={{ status: StudentStatus.dropped }}
        onSuccess={onSuccess}
        students={rowDataSelection}
        title="Mark drop off"
        validation={(student) => student.status === StudentStatus.enrolled}
        isSelectAllPagesActive={isSelectAllPagesActive}
        filters={{
          query: activeUserNameQuery,
          schools: activeSelectedSchools,
          orgs: activeSelectedOrgs,
          grades: activeSelectedGrades,
          statuses: activeSelectedStatuses,
        }}
        actionName={UpdateStudentsBulkActions.drop}
      />
      <StudentBulkUpdate
        confirmMessage="Are you sure you want to conclude the program for"
        invalidMessage="Only students with no program concluded status can be selected."
        options={{ status: StudentStatus.program_concluded }}
        onSuccess={onSuccess}
        students={rowDataSelection}
        title="Mark program concluded"
        validation={(student) => student.status !== StudentStatus.program_concluded}
        isSelectAllPagesActive={isSelectAllPagesActive}
        filters={{
          query: activeUserNameQuery,
          schools: activeSelectedSchools,
          orgs: activeSelectedOrgs,
          grades: activeSelectedGrades,
          statuses: activeSelectedStatuses,
        }}
        actionName={UpdateStudentsBulkActions.conclude}
      />
      <DropdownMenuItem
        onSelect={async (e) => {
          e.preventDefault();
          try {
            let studentsToExport: StudentListRow[];

            if (isSelectAllPagesActive) {
              // Export all students with filters applied (without pagination)
              const result = await refetchAllStudents();
              studentsToExport = result.data?.students || [];
            } else {
              // Export only selected rows from current page
              studentsToExport = table.getSelectedRowModel().rows.map((r) => r.original);
            }

            await generateQrCodesPdf(studentsToExport, timeZone);
            toast({
              title: 'QR codes PDF generated successfully',
            });
          } catch (error) {
            console.error('Failed to generate QR codes PDF:', error);
            toast({
              title: 'Failed to generate QR codes PDF',
              description: error instanceof Error ? error.message : 'An error occurred',
              variant: 'destructive',
            });
          }
        }}
      >
        Export QR code as PDF
      </DropdownMenuItem>
    </>
  );

  const topBarRightComponents = canCreateUsers(actingUser) && (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button className="ml-auto hidden h-8 lg:flex">
          Add students
          <ChevronDown className="mr-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <CreateStudent
          onSuccess={refetch}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Add a student</DropdownMenuItem>}
        />
        <CreateStudentsByCsv
          onSuccess={refetch}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Import from CSV</DropdownMenuItem>}
        />
        <BulkUpdateLearningPathByCsv
          onSuccess={refetch}
          trigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              Bulk update learning path & starting lesson
            </DropdownMenuItem>
          }
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
  return (
    <InfoPage headerText="Students" tabs={studentsTabs()}>
      <EntityTable
        table={table}
        showPagination={true}
        paginationOptions={[10, 20, 30, 40, 50, 100]}
        autoManagedColumnIds={['selection']}
        isLoading={isPending}
        hasError={!!error}
        emptyState={'No students found'}
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents }}
        bulkActionsMenuItems={bulkActionsMenuItems}
        topBarRightComponents={topBarRightComponents}
        isSelectAllPagesActive={isSelectAllPagesActive}
        onSelectAllPagesChange={handleSelectAllPagesChange}
        totalRowCount={totalRowCount}
      />
    </InfoPage>
  );
}
